import React, {useCallback, useRef} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useGetIsOfferSoldOut} from 'hooks/useGetIsOfferSoldOut/useGetIsOfferSoldOut'
import {getUserCountryCode} from 'modules/meta/selectors'
import {
  getRoomDetailOverlay,
  getRoomDetailOverlayVisibility
} from 'modules/overlay/selectors'
import {hideOverlay} from 'modules/overlay/slice'
import {
  getNumberOfGuests,
  getNumberOfRooms,
  getOptimizedRoomsConfig,
  getSearchParameters
} from 'modules/sapiSearch/selectors'
import {
  getAccommodationPageHotel,
  getFAQData,
  getRoomById
} from 'modules/searchApi/selectors'
import {RootState} from 'store'
import {imageProvider} from 'utils/imageProvider'

import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {useInView} from '@vio/core/src/_web/utils/browser/hooks/useInView'
import {roomsStringToRoomsSplit} from '@vio/core/src/room/business/roomConfiguration'
import {RoomDetailsOverlay} from '@vio/shared/src/accommodation/RoomDetailsOverlay'
import {AvailableDealsSection} from '@vio/shared/src/accommodation/RoomDetailsOverlay/AvailableDealsSection'
import {useRoomDisplayName} from '@vio/shared/src/accommodation/RoomDisplayName'

import {useGuestSplit} from '../Rooms/hooks/useGuestSplit'
import {useRoomTitleConfig} from '../Rooms/hooks/useRoomTitleConfig'
import {RoomOffersList} from '../Rooms/RoomOffersList'
import {OverlayCompactFooter} from './OverlayCompactFooter'

const OFFERS_SECTION_SCROLL_MARGIN = 20

export const RoomsDetailOverlayDatefull = () => {
  const {brand} = useBrand()
  const {ref: offersSectionRef, inView: offersSectionInView} = useInView()
  const offersSectionElementRef = useRef<HTMLDivElement | null>(null)
  const roomsDetailsOverlayState = useSelector(getRoomDetailOverlay)
  const isRoomsDetailsOverlayOpen = useSelector(getRoomDetailOverlayVisibility)
  const {hotelId, roomId} = roomsDetailsOverlayState || {}
  const faqData = useSelector(state => getFAQData(state, hotelId))
  const {checkIn, checkOut} = useSelector(getSearchParameters) || {}
  const numberOfRooms = useSelector((state: RootState) =>
    getNumberOfRooms(state, hotelId)
  )
  const numberOfGuests = useSelector(getNumberOfGuests)
  const room = useSelector(state => getRoomById(state, roomId))
  const roomsString = useSelector(state =>
    getOptimizedRoomsConfig(state, hotelId)
  )
  const roomsSplit = roomsStringToRoomsSplit(roomsString)
  const guestSplit = useGuestSplit(hotelId)
  const roomDisplayName = useRoomDisplayName(room?.name, {
    numberOfGuests,
    numberOfRooms
  })
  const {
    isEnabled: isRoomTitleRuleEnabled,
    numberOfRooms: roomsAmount,
    hasAllRoomsHaveLabel
  } = useRoomTitleConfig(room, roomsSplit.length)
  const hotelInfo = useSelector(getAccommodationPageHotel)
  const childPolicy = hotelInfo?.childPolicy
  const firstOffer = room?.offers ? room?.offers[0] : null
  const dispatch = useDispatch()
  const isSoldOut = useGetIsOfferSoldOut({offer: firstOffer})

  const userCountry = useSelector(getUserCountryCode)

  const isOverlayOpen = isRoomsDetailsOverlayOpen && Boolean(room)

  const availableDealsSectionProps = {
    checkIn,
    checkOut,
    numberOfGuests,
    numberOfRooms,
    guestSplit,
    numberOfOffers: room?.offers?.length ?? 0,
    roomOffersListSlot:
      isSoldOut || !room ? null : (
        <RoomOffersList room={room} isMountedOnOverlay />
      )
  }

  const setOffersSectionRef = useCallback(
    (node: HTMLDivElement | null) => {
      offersSectionElementRef.current = node
      offersSectionRef(node)
    },
    [offersSectionRef]
  )

  const onViewDealsClick = () => {
    offersSectionElementRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    })
  }

  const stickyFooter =
    room && !isSoldOut ? (
      <OverlayCompactFooter
        offer={room.offers[0]}
        isFooterVisible={!offersSectionInView}
        hotelId={hotelId}
        viewDealsClickHandler={onViewDealsClick}
      />
    ) : null

  return (
    <RoomDetailsOverlay
      isOpen={isOverlayOpen}
      room={room}
      roomDisplayName={roomDisplayName}
      roomsAmount={isRoomTitleRuleEnabled ? roomsAmount : undefined}
      onClose={() => dispatch(hideOverlay())}
      imageProvider={imageProvider}
      userCountry={userCountry}
      childPolicy={childPolicy}
      brand={brand}
      faqData={faqData}
      roomsSplit={roomsSplit}
      hasAllRoomsHaveLabel={hasAllRoomsHaveLabel}
      offersSectionSlot={
        <div
          ref={setOffersSectionRef}
          style={{scrollMarginTop: `${OFFERS_SECTION_SCROLL_MARGIN}px`}}
        >
          <AvailableDealsSection
            availableDealsSectionProps={availableDealsSectionProps}
          />
        </div>
      }
      footerSlot={stickyFooter}
    />
  )
}
